import { useState } from "react";
import { Copy, Check, Plane } from "lucide-react";

const BOOKING_URL = "https://beautesolutions.intakeq.com/booking";
const PORTAL_URL = "https://beautesolutions.intakeq.com/portal";
const ADDRESS = "My Salon Suites, 9823 W Northern Ave, Suite 125, Peoria, AZ 85345";
const MAP_EMBED =
  "https://maps.google.com/maps?q=Beaute%20Solutions%2C%209823%20W%20Northern%20Ave%20Ste%20125%2C%20Peoria%2C%20AZ%2085345&t=&z=16&ie=UTF8&iwloc=&output=embed";

export default function Footer() {
  const [copied, setCopied] = useState(false);

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(ADDRESS);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setCopied(false);
    }
  };

  return (
    <footer className="bg-espresso text-cream" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-10">
        <div className="text-center max-w-2xl mx-auto" data-testid="footer-cta">
          <h2 className="font-serif text-3xl sm:text-5xl leading-tight">Come see us — or start with a photo.</h2>
          <p className="mt-5 text-cream/60 leading-relaxed">
            Booking is our only point of contact, so every enquiry gets a proper
            consultation instead of a rushed phone call.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-photo-consultation"
              className="inline-flex items-center rounded-full bg-gold text-espresso px-8 py-4 text-sm font-semibold transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-cream"
            >
              Free photo consultation
            </a>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-online-consultations"
              className="inline-flex items-center rounded-full border border-cream/25 text-cream px-8 py-4 text-sm font-semibold transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-gold hover:text-gold"
            >
              Free online consultations
            </a>
          </div>
          <p className="mt-8 text-sm text-cream/60">
            Already booked?{" "}
            <a
              href={PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-portal-link"
              className="font-semibold text-gold underline decoration-gold/40 underline-offset-4 hover:text-cream transition-colors"
            >
              Open your consult portal
            </a>{" "}
            to submit photos and messages.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-2 gap-10 items-stretch" data-testid="footer-visit">
          <div className="flex flex-col justify-center">
            <div data-testid="footer-facility">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-4">Inside the studio</p>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { src: "/images/facility-1.jpg", alt: "Beauté Solutions — outside the My Salon Suite building in Peoria, AZ" },
                  { src: "/images/facility-2.jpg", alt: "Beauté Solutions — hallway and clinic entrance" },
                  { src: "/images/facility-3.jpg", alt: "Beauté Solutions — treatment room interior" },
                ].map((p) => (
                  <img
                    key={p.src}
                    src={p.src}
                    alt={p.alt}
                    loading="lazy"
                    className="w-full h-32 sm:h-40 object-cover rounded-2xl border border-linedark"
                  />
                ))}
              </div>
            </div>
            <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-4">Clinic address</p>
            <div data-testid="footer-address">
              <p className="font-serif italic text-xl sm:text-2xl text-gold">My Salon Suites</p>
              <p className="mt-2 font-serif text-2xl sm:text-3xl leading-snug text-cream">
                9823 W Northern Ave, Suite 125<br />Peoria, AZ 85345
              </p>
              <p className="mt-3 text-sm text-cream/60">(Inside Park West Plaza on Northern Ave)</p>
            </div>
            <button
              onClick={copyAddress}
              data-testid="copy-address-btn"
              className="mt-6 inline-flex w-fit items-center gap-2 rounded-full border border-cream/25 px-5 py-2.5 text-xs font-semibold text-cream transition-[border-color,color,background-color] duration-300 hover:border-gold hover:text-gold"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied!" : "Copy address"}
            </button>
            <div className="mt-8" data-testid="footer-phone">
              <a
                href="tel:+16026094341"
                data-testid="footer-phone-link"
                className="inline-block font-serif text-2xl sm:text-3xl text-cream transition-colors duration-300 hover:text-gold"
              >
                602-609-4341
              </a>
              <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-cream/50">
                For information only — booking is online
              </p>
            </div>
            <div className="mt-8 rounded-2xl border border-gold/25 bg-linedark/40 p-6" data-testid="footer-flying-in">
              <p className="flex items-center gap-2 text-sm font-semibold text-gold">
                <Plane size={15} /> Flying in?
              </p>
              <p className="mt-3 text-sm text-cream/70 leading-relaxed">
                Most clients can fly in and home the same day if they have a single
                flight — it's about a 30 to 40-minute Uber each way from Phoenix Sky
                Harbor International Airport (PHX). Others may need to stay the night.
                Please plan for about five hours between your landing time and your
                departure.
              </p>
            </div>
          </div>
          <div>
            <div className="rounded-2xl overflow-hidden border border-linedark h-full min-h-[18rem]" data-testid="footer-map">
              <iframe
                title="Beauté Solutions — clinic map"
                src={MAP_EMBED}
                className="w-full h-full grayscale-[0.2]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>

        <p className="mt-14 text-center text-sm text-cream/60" data-testid="footer-no-phone-note">
          Booking stays online — it's how every enquiry gets a proper consultation.
        </p>
        <p className="mt-10 text-center" data-testid="footer-tagline">
          <span className="font-serif italic text-2xl text-gold">My clients are for life.</span>
          <span className="block mt-2 text-xs font-semibold text-cream/50">— Sherry Corvino</span>
        </p>

        <div className="mt-10 pt-8 border-t border-linedark flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-mono text-[10px] tracking-[0.2em] text-cream/40 uppercase">
            © {new Date().getFullYear()} Beauté Solutions. All rights reserved.
          </p>
          <div className="flex gap-6 font-mono text-[10px] uppercase tracking-[0.2em]">
            <span data-testid="footer-link-home" className="text-cream/50">Home</span>
            <span data-testid="footer-link-services" className="text-cream/50">Services</span>
            <span data-testid="footer-link-about" className="text-cream/50">About Sherry</span>
          </div>
        </div>
        <p className="mt-6 text-center text-[11px] text-cream/35 leading-relaxed max-w-3xl mx-auto">
          Content on this site is educational and is not medical advice. Candidacy,
          session counts and results are confirmed at consultation. Results vary from
          person to person.
        </p>
      </div>
    </footer>
  );
}
